import { Controller, type Control, type FieldPath } from 'react-hook-form'
import { DatePicker } from '@/components/ui/DatePicker'
import { cn } from '@lib/utils/helpers'
import type { ResidentDefaultFormValues } from '../types'

const dateShell =
  'mt-1 h-10 w-full rounded-lg border bg-white px-3 text-sm outline-none transition focus:border-button focus:ring-2 focus:ring-blue-100'

export function ResidentDateField<N extends FieldPath<ResidentDefaultFormValues>>({
  control,
  name,
  disabled,
  readOnly,
  error,
  allowFutureDates = true,
  className,
  dataTestId,
  onUserChange,
}: {
  control: Control<ResidentDefaultFormValues>
  name: N
  disabled?: boolean
  readOnly?: boolean
  error?: boolean
  allowFutureDates?: boolean
  className?: string
  /** Defaults to `resident-default-field-{field}` with underscores → hyphens. */
  dataTestId?: string
  /** Called after the user changes the value (not on programmatic reset). */
  onUserChange?: (value: string) => void
}) {
  const resolvedDataTestId =
    dataTestId ?? `resident-default-field-${String(name).replace(/_/g, '-')}`
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <DatePicker
          value={typeof field.value === 'string' ? field.value : ''}
          onChange={(next) => {
            field.onChange(next)
            onUserChange?.(next)
          }}
          onBlur={field.onBlur}
          disabled={disabled}
          readOnly={readOnly}
          allowFutureDates={allowFutureDates}
          showHelperText={false}
          popperFixed
          dataTestId={resolvedDataTestId}
          className={cn(
            dateShell,
            error ? 'border-red-500' : 'border-slate-200',
            disabled && 'cursor-not-allowed opacity-70',
            readOnly && !disabled && 'cursor-default',
            className,
          )}
        />
      )}
    />
  )
}
